import { PageChangedEvent } from '@iris/common/modules/grid/ag-grid/extensions/pagination/page-changed-event';
import { AgGridPagingPanelComponent } from '@iris/common/modules/grid/ag-grid/extensions/pagination/paging-panel.component';
import { AgGridAngular } from 'ag-grid-angular';
import { difference } from 'lodash';
import { AfterViewInit, ComponentRef, Directive, ElementRef, EventEmitter, Input, OnChanges, OnDestroy, Output, ViewContainerRef, inject } from '@angular/core';
import { takeUntil, tap } from 'rxjs/operators';
import { Observable, Subject } from 'rxjs';
import { AgGridSandbox } from '@iris/common/modules/grid/ag-grid/ag-grid.sandbox';
import { IrisGridMode } from '@iris/common/modules/grid/ag-grid/models/iris-grid-mode';

@Directive({
  selector: `ag-grid-angular[irisPagination]`,
  providers: [AgGridSandbox],
  standalone: false,
})
export class AgGridAddPagingPanelDirective implements OnChanges, AfterViewInit, OnDestroy {
  private readonly agGrid = inject(AgGridAngular);
  protected readonly viewContainerRef = inject(ViewContainerRef);
  private readonly elementRef = inject(ElementRef);
  private readonly gridSandbox = inject(AgGridSandbox);

  @Input('irisPagination') pagination = false;
  @Input('irisGridMode') gridMode: IrisGridMode;
  @Input('irisPageSize') pageSize: number;
  @Input('irisPage') page = 1;
  @Input('irisShowPageSelector') showPageSelector: boolean;
  @Input('irisPageSelectorWidth') pageSelectorWidth: boolean;
  @Input('irisPageSizes') pageSizes: number[];
  @Input('irisTotalItemsCount') totalItemsCount: number;
  @Input('irisShowPageSizeSelector') showPageSizeSelector: boolean;
  @Input('irisShowNumberOfCurrentItems') showNumberOfCurrentItems: boolean;
  @Input('irisSelectedRowsCount') selectedRowsCount: number;
  @Input('irisLastPage') lastPage: boolean;
  @Output() irisPageChanged = new EventEmitter<PageChangedEvent>();
  @Output() irisPageSizeChanged = new EventEmitter<number>();

  get currentPage(): number { return this._pagingPanelComponentRef?.instance.page ?? this.page; }

  _pagingPanelComponentRef: ComponentRef<AgGridPagingPanelComponent>;
  destroyedSubject = new Subject<void>();

  ngOnChanges(changes: SimpleChanges<this>): void {
    if (changes.pagination) {
      this.agGrid.pagination = !!this.pagination;
      this.agGrid.suppressPaginationPanel = true;
      return;
    }
    if (this._pagingPanelComponentRef) {
      const skippingProps: (keyof this)[] = ['pagination'];
      const changedProps = Object.keys(changes) as (keyof this)[];
      this.copyPropsToPanel(difference(changedProps, skippingProps) as (keyof AgGridPagingPanelComponent)[]);
    }
  }

  ngAfterViewInit(): void {
    if (!this.pagination) return;

    this._pagingPanelComponentRef = this._renderPagingPanel();
    this._pagingPanelComponentRef.instance.irisPageChanged = this.irisPageChanged;
    this._pagingPanelComponentRef.instance.irisPageSizeChanged = this.irisPageSizeChanged;
    const request$: Observable<unknown> = this.gridMode === 'SERVER' ? this.agGrid.gridReady : this.gridSandbox.updatePropsForClientGrid$;

    request$.pipe(
      tap(() => this.copyPropsToPanel([
        'agGrid',
        'page',
        'pageSize',
        'pageSizes',
        'totalItemsCount',
        'gridMode',
        'showPageSelector',
        'pageSelectorWidth',
        'showPageSizeSelector',
        'showNumberOfCurrentItems',
        'selectedRowsCount',
        'lastPage',
      ])),
      takeUntil(this.destroyedSubject),
    ).subscribe();
  }

  ngOnDestroy(): void {
    this.destroyedSubject.next();
    this.destroyedSubject.complete();
    this._pagingPanelComponentRef?.destroy();
  }

  _renderPagingPanel(): ComponentRef<AgGridPagingPanelComponent> {
    const componentRef = this.viewContainerRef.createComponent(AgGridPagingPanelComponent);
    this._injectPagingPanelIntoAgGrid(componentRef);
    return componentRef;
  }

  _injectPagingPanelIntoAgGrid(componentRef: ComponentRef<unknown>): void {
    const elements = (this.elementRef.nativeElement as HTMLElement).getElementsByClassName('ag-paging-panel');
    const pagingPanel = elements.length ? (elements[0] as HTMLElement) : null;
    pagingPanel?.parentElement?.appendChild(componentRef.location.nativeElement);
  }

  copyPropsToPanel(props: (keyof AgGridPagingPanelComponent)[]): void {
    const component = this._pagingPanelComponentRef?.instance;
    if (!component) { return; }
    props
      .filter(prop => prop !== undefined)
      .forEach(prop => component[prop as string] = this[prop]);
    component.refresh(props);
  }
}

@Directive({
  selector: `ag-grid-angular[irisPaginationStandalone]`,
  providers: [AgGridSandbox],
  standalone: true,
})
export class AgGridAddPagingPanelStandaloneDirective extends AgGridAddPagingPanelDirective {}
